<template>
  <v-card class="card-pad pa-4">
    <v-form ref="form">
      <v-row class="ma-0" dense>
        <v-col cols="12">
          <div class="d-flex align-center">
            <h2>Gerenciar Custo Evitado Unitário</h2>

            <v-spacer></v-spacer>

            <v-btn icon @click="$emit('close')">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-divider class="my-4"></v-divider>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="label">
            Custo
            <span
              v-if="shouldDisableGeneralCost"
              class="grey--text text--darken-1"
            >
              (Soma automática dos setores)
            </span>
          </label>
          <v-currency-field
            v-model="item.cost"
            class="primary-input"
            placeholder="0000"
            validate-on-blur
            :rules="[
              (v) => shouldDisableGeneralCost || !!v || 'Custo obrigatório',
            ]"
            :disabled="shouldDisableGeneralCost"
            :readonly="shouldDisableGeneralCost"
            hide-details="auto"
            outlined
          >
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-currency-field>
          <div
            v-if="shouldDisableGeneralCost && totalCostFromDepartments > 0"
            class="mt-2"
          >
            <small class="grey--text">
              <v-icon small color="grey">mdi-information-outline</v-icon>
              <span>
                Soma de {{ selectedDepartmentObjects.length }} setores
              </span>
            </small>
          </div>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="label">Natureza do custo</label>
          <v-autocomplete
            append-icon="mdi-chevron-down"
            class="primary-input"
            v-model="item.avoidedCostNatureId"
            :items="naturezas"
            :loading="naturezaLoading"
            item-text="nature"
            item-value="id"
            placeholder="Natureza do custo"
            :rules="[(v) => !!v || 'Custo obrigatório']"
            outlined
            hide-details="auto"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12">
          <label class="label">Descrição</label>
          <v-textarea
            class="primary-input"
            v-model="item.description"
            placeholder="Descrição"
            outlined
            hide-details="auto"
          ></v-textarea>
        </v-col>
        <v-col cols="12">
          <label class="label ml-1">{{ $t('Data') }}</label>
          <div class="d-flex mb-4">
            <date-picker
              ref="datepicker"
              style="width: 100%"
              v-model="item.date"
              mode="dateTime"
              is24hr
              :popover="{ visibility: 'click' }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  :class="dateError ? 'date-input-error' : 'date-input'"
                  :value="inputValue"
                  v-on="inputEvents"
                  v-mask="['##/##/#### ##:##']"
                  @focus="setDateNowDate"
                />
                <span class="date-span-error" v-if="dateError">{{
                  $t('Data é requerida')
                }}</span>
              </template>
            </date-picker>
          </div>
        </v-col>
        <v-col cols="12">
          <label class="label">Unidade</label>
          <v-autocomplete
            append-icon="mdi-chevron-down"
            class="primary-input"
            v-model="item.unidades"
            :items="unidades"
            :loading="unidLoading"
            :rules="[(v) => !!v || 'Custo obrigatório']"
            item-text="name"
            item-value="id"
            placeholder="Unidade"
            outlined
            hide-details="auto"
            multiple
            deletable-chips
            clearable
            ><template v-slot:selection="{ attrs, item, select, selected }">
              <v-chip
                v-bind="attrs"
                :input-value="selected"
                close
                color="#BBCF20"
                text-color="#fff"
                small
                @click="select"
                @click:close="removeUnidade(item)"
              >
                {{ item.name }}
              </v-chip>
            </template></v-autocomplete
          >
        </v-col>
        <v-col cols="12">
          <label class="label">Setor</label>
          <v-autocomplete
            v-if="!departmentLoading"
            append-icon="mdi-chevron-down"
            class="primary-input"
            v-model="item.departments"
            :items="filteredDepartments"
            item-text="name"
            item-value="id"
            :rules="[(v) => !!v || 'Setor obrigatório']"
            placeholder="Setor"
            multiple
            deletable-chips
            clearable
            outlined
            hide-details="auto"
            :disabled="!item.unidades || item.unidades.length === 0"
            :no-data-text="
              !item.unidades || item.unidades.length === 0
                ? 'Selecione uma unidade primeiro'
                : 'Nenhum setor encontrado'
            "
          >
            <template v-slot:selection="{ attrs, item, select, selected }">
              <v-chip
                v-bind="attrs"
                :input-value="selected"
                close
                color="#BBCF20"
                text-color="#fff"
                small
                @click="select"
                @click:close="removeDepartment(item)"
              >
                {{ item.name }} ({{ item.unidade ? item.unidade.name : '' }})
              </v-chip>
            </template>

            <template v-slot:item="{ item }">
              <span>
                {{ item.name }} ({{ item.unidade ? item.unidade.name : '' }})
              </span>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col
          cols="12"
          v-show="item.departments && item.departments.length > 1"
        >
          <div class="pa-4 rounded-lg grey lighten-4">
            <strong>Defina o custo para cada setor:</strong>

            <v-divider class="my-4"></v-divider>

            <div
              v-for="dept in selectedDepartmentObjects"
              :key="dept.id"
              class="mb-3"
            >
              <label class="label">
                Custo do {{ dept.name }} ({{
                  dept.unidade ? dept.unidade.name : ''
                }})
              </label>
              <v-currency-field
                v-model="item.departmentCosts[dept.id]"
                class="primary-input"
                :placeholder="`Custo para ${dept.name}`"
                validate-on-blur
                :rules="[
                  (v) =>
                    (item.departments && item.departments.length <= 1) ||
                    !!v ||
                    `Custo de ${dept.name} obrigatório`,
                ]"
                hide-details="auto"
                outlined
                @input="onDeptInput(dept.id, $event)"
                @blur="onDeptBlur"
              >
                <template #message="{ message }">
                  {{ $t(message) }}
                </template>
              </v-currency-field>
            </div>
          </div>
        </v-col>

        <v-col cols="12">
          <label class="label">Centro de custo</label>
          <v-autocomplete
            append-icon="mdi-chevron-down"
            class="primary-input"
            v-model="item.costCenters"
            item-text="name"
            item-value="id"
            :items="costCenters"
            :loading="costCenterLoading"
            :rules="[(v) => !!v || 'Custo obrigatório']"
            placeholder="Centro de custo"
            outlined
            hide-details="auto"
            multiple
            deletable-chips
            clearable
            @blur="onCcBlur(cc.id)"
            ><template v-slot:selection="{ attrs, item, select, selected }">
              <v-chip
                v-bind="attrs"
                :input-value="selected"
                close
                color="#BBCF20"
                text-color="#fff"
                small
                @click="select"
                @click:close="removeCostCenter(item)"
              >
                {{ item.name }}
              </v-chip>
            </template></v-autocomplete
          >
        </v-col>

        <v-col
          cols="12"
          v-show="item.costCenters && item.costCenters.length > 1"
        >
          <div class="pa-4 rounded-lg grey lighten-4">
            <div class="d-flex align-center mb-4">
              <strong>Defina o custo para cada centro de custo</strong>
              <v-spacer></v-spacer>
              <div class="text-right">
                <div class="caption grey--text">Valor a ser dividido:</div>
                <div class="title primary--text">
                  R$ {{ totalCostFromDepartments.toFixed(2) }}
                </div>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <div
              v-for="cc in selectedCostCenterObjects"
              :key="cc.id"
              class="mb-3"
            >
              <label class="label">
                Custo do {{ cc.name }}
                <span
                  v-if="
                    getRemainingForCostCenter(cc.id) < totalCostFromDepartments
                  "
                  class="grey--text text--darken-1"
                >
                  (Máximo disponível: R$
                  {{ getRemainingForCostCenter(cc.id).toFixed(2) }})
                </span>
              </label>
              <v-currency-field
                v-model="item.costCenterCosts[cc.id]"
                class="primary-input"
                :placeholder="`Custo para ${cc.name}`"
                validate-on-blur
                :rules="[
                  (v) =>
                    (item.costCenters && item.costCenters.length <= 1) ||
                    !!v ||
                    `Custo de ${cc.name} obrigatório`,
                ]"
                hide-details="auto"
                outlined
                @input="onCcInput(cc.id, $event)"
                @blur="onCcBlur"
              >
                <template #message="{ message }">
                  {{ $t(message) }}
                </template>
              </v-currency-field>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="d-flex align-center">
              <div>
                <strong>Total distribuído:</strong>
                R$ {{ totalCostFromCostCenters.toFixed(2) }}
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-form>

    <div class="d-flex justify-end mt-4">
      <v-btn class="ma-1 secondary-button" depressed @click="$emit('close')">
        Cancelar
      </v-btn>
      <v-btn class="ma-1 primary-button" depressed @click="submit">
        Salvar
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import ApiService from '../../../../../../services/ApiService'

export default {
  name: 'UnitaryAvoidedCostsManage',

  props: {
    itemProp: Object,
  },

  data() {
    return {
      item: {},
      loading: false,
      isEditing: false,
      apiService: new ApiService(),
      unidades: [],
      departments: [],
      costCenters: [],
      naturezas: [],
      unidLoading: false,
      departmentLoading: false,
      costCenterLoading: false,
      naturezaLoading: false,
      institution: {},
      dateError: false,
      ccInputTimeout: null,
    }
  },

  async created() {
    this.user = JSON.parse(localStorage.getItem('user'))
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.item.institutionId = this.institution.id

    await this.getUnidades()
    await this.getCostCenters() // Carrega todos os centros de custo inicialmente
    await this.getNaturezas()
    await this.getDepartments()

    if (this.itemProp?.id) {
      this.isEditing = true
      await this.getItem()
    }
  },

  beforeDestroy() {
    if (this.ccInputTimeout) {
      clearTimeout(this.ccInputTimeout)
    }
  },

  computed: {
    filteredDepartments() {
      if (!this.item.unidades?.length) return []
      return this.departments.filter((d) =>
        this.item.unidades.includes(d.unidadeId)
      )
    },

    shouldDisableGeneralCost() {
      const hasMultipleDepartments = this.item.departments?.length > 1
      return hasMultipleDepartments
    },

    selectedDepartmentObjects() {
      if (!this.item.departments?.length) return []
      return this.departments.filter((d) =>
        this.item.departments.includes(d.id)
      )
    },

    selectedCostCenterObjects() {
      if (!this.item.costCenters?.length) return []
      return this.costCenters.filter((c) =>
        this.item.costCenters.includes(c.id)
      )
    },

    totalCostFromDepartments() {
      if (!this.item.departmentCosts || this.item.departments?.length <= 1) {
        return parseFloat(this.item.cost) || 0
      }
      return Object.values(this.item.departmentCosts).reduce(
        (sum, val) => sum + (parseFloat(val) || 0),
        0
      )
    },

    totalCostFromCostCenters() {
      if (!this.item.costCenterCosts || this.item.costCenters?.length <= 1)
        return 0
      return Object.values(this.item.costCenterCosts).reduce(
        (sum, val) => sum + (parseFloat(val) || 0),
        0
      )
    },

    remainingCost() {
      return Math.max(
        0,
        this.totalCostFromDepartments - this.totalCostFromCostCenters
      )
    },

    finalCost() {
      return this.totalCostFromDepartments
    },
  },

  methods: {
    parseCurrency(value) {
      if (!value) return 0
      if (typeof value === 'number') return value
      let s = String(value).replace(/[^\d\-,.]/g, '')
      if (s.includes(',') && s.includes('.')) {
        s = s.replace(/\./g, '').replace(/,/g, '.')
      } else if (s.includes(',') && !s.includes('.')) {
        s = s.replace(/,/g, '.')
      }
      return parseFloat(s) || 0
    },

    getRemainingForCostCenter(currentCcId) {
      const total = this.totalCostFromDepartments
      const usedBefore = Object.entries(this.item.costCenterCosts || {})
        .filter(([id]) => parseInt(id) !== currentCcId)
        .reduce((sum, [, val]) => sum + (parseFloat(val) || 0), 0)
      return Math.max(0, total - usedBefore)
    },

    onDeptInput(deptId, val) {
      const num = this.parseCurrency(val?.target?.value ?? val)
      if (!this.item.departmentCosts)
        this.$set(this.item, 'departmentCosts', {})
      this.$set(this.item.departmentCosts, deptId, num)
    },

    onDeptBlur() {
      this.$forceUpdate()
      this.recalculateDeptCost()
    },

    onCcInput(ccId, val) {
      if (this.ccInputTimeout) {
        clearTimeout(this.ccInputTimeout)
      }

      let num = this.parseCurrency(val?.target?.value ?? val)

      const maxAvailable = this.getRemainingForCostCenter(ccId)
      if (num > maxAvailable) {
        num = maxAvailable
      }

      if (!this.item.costCenterCosts) {
        this.$set(this.item, 'costCenterCosts', {})
      }
      this.$set(this.item.costCenterCosts, ccId, num)

      this.ccInputTimeout = setTimeout(() => {
        this.$forceUpdate()
      }, 100)
    },

    onCcBlur(ccId) {
      if (this.ccInputTimeout) {
        clearTimeout(this.ccInputTimeout)
      }

      if (
        this.item.costCenterCosts &&
        this.item.costCenterCosts[ccId] !== undefined
      ) {
        let num = parseFloat(this.item.costCenterCosts[ccId]) || 0
        const maxAvailable = this.getRemainingForCostCenter(ccId)

        if (num > maxAvailable) {
          this.$set(this.item.costCenterCosts, ccId, maxAvailable)
        }
      }
      this.$forceUpdate()
    },

    recalculateDeptCost() {
      if (!this.shouldDisableGeneralCost) return

      const deptSum = Object.values(this.item.departmentCosts || {}).reduce(
        (s, v) => s + (parseFloat(v) || 0),
        0
      )

      this.$set(this.item, 'cost', deptSum)
    },

    async getItem() {
      try {
        const res = await this.apiService.get(
          `avoidedCost/getById/${this.itemProp.id}`
        )
        const ac = res.content

        this.item = {
          ...ac,
          unidades: ac.unidades?.map((u) => u.unidadeId) || [],
          departments: ac.departments?.map((d) => d.departmentId) || [],
          costCenters: ac.costCenters?.map((c) => c.costCenterId) || [],
          departmentCosts: Object.fromEntries(
            (ac.departments || []).map((d) => [d.departmentId, d.cost])
          ),
          costCenterCosts: Object.fromEntries(
            (ac.costCenters || []).map((c) => [c.costCenterId, c.cost])
          ),
        }

        this.$nextTick(() => {
          if (this.shouldDisableGeneralCost)
            this.item.cost = this.totalCostFromDepartments
        })
      } catch (e) {
        console.error('Erro ao carregar item:', e)
      }
    },

    removeDepartment(dep) {
      const idx = this.item.departments.indexOf(dep.id)
      if (idx > -1) {
        this.item.departments.splice(idx, 1)
        this.$delete(this.item.departmentCosts, dep.id)
      }
    },

    removeCostCenter(cc) {
      const idx = this.item.costCenters.indexOf(cc.id)
      if (idx > -1) {
        this.item.costCenters.splice(idx, 1)
        this.$delete(this.item.costCenterCosts, cc.id)
      }
    },

    removeUnidade(unid) {
      const idx = this.item.unidades.indexOf(unid.id)
      if (idx > -1) this.item.unidades.splice(idx, 1)
    },

    submit() {
      this.$forceUpdate()
      this.recalculateDeptCost()

      if (!this.$refs.form.validate()) return

      this.loading = true

      const depMulti = this.item.departments?.length > 1
      const ccMulti = this.item.costCenters?.length > 1

      const departments =
        this.item.departments?.map((id) => ({
          departmentId: id,
          cost: depMulti
            ? parseFloat(this.item.departmentCosts[id]) || 0
            : parseFloat(this.item.cost) || 0,
        })) || []

      const costCenters =
        this.item.costCenters?.map((id) => ({
          costCenterId: id,
          cost: ccMulti ? parseFloat(this.item.costCenterCosts[id]) || 0 : 0,
        })) || []

      const payload = {
        id: this.item.id,
        cost: this.finalCost,
        description: this.item.description,
        type: 0,
        date: this.item.date,
        avoidedCostNatureId: this.item.avoidedCostNatureId,
        institutionId: this.institution.id,
        createdByUserId: this.user.id,
        unidades: this.item.unidades?.map((id) => ({ unidadeId: id })) || [],
        departments,
        costCenters,
      }

      const call = this.isEditing
        ? this.apiService.put('avoidedCost/edit', payload)
        : this.apiService.post('avoidedCost/add', payload)

      call
        .then(() => {
          this.loading = false
          this.$emit('confirm')
        })
        .catch((err) => {
          console.error('Erro ao salvar:', err)
          this.loading = false
        })
    },

    async getUnidades() {
      this.unidLoading = true
      try {
        const res = await this.apiService.get(
          `unidade/listByUserAndInstitution/${this.institution.id}`
        )
        this.unidades = res.content
      } finally {
        this.unidLoading = false
      }
    },

    async getDepartments() {
      this.departmentLoading = true
      try {
        const res = await this.apiService.get(
          `department/list/${this.institution.id}`
        )
        this.departments = res.content
      } finally {
        this.departmentLoading = false
      }
    },

    async getCostCenters() {
      this.costCenterLoading = true
      try {
        let allCostCenters = []
        
        if (!this.item.unidades || this.item.unidades.length === 0) {
          // Se não há unidades selecionadas, buscar todos os centros de custo da instituição
          const res = await this.apiService.get(
            `CostCenter/listActive/${this.institution.id}`
          )
          if (res.content) {
            allCostCenters = res.content
          }
        } else {
          // Se há unidades selecionadas, buscar centros de custo para cada unidade
          for (const unidadeId of this.item.unidades) {
            const res = await this.apiService.get(
              `costCenter/listActiveByUnidade/${this.institution.id}/${unidadeId}`
            )
            if (res.content) {
              allCostCenters = [...allCostCenters, ...res.content]
            }
          }
          
          // Remover duplicatas baseado no ID
          allCostCenters = allCostCenters.filter((center, index, self) => 
            index === self.findIndex(c => c.id === center.id)
          )
        }
        
        this.costCenters = allCostCenters
      } catch (error) {
        console.error('Erro ao buscar centros de custo:', error)
        this.costCenters = []
      } finally {
        this.costCenterLoading = false
      }
    },

    async getNaturezas() {
      this.naturezaLoading = true
      try {
        const res = await this.apiService.get(
          `AvoidedCostNature/list/${this.institution.id}`
        )
        this.naturezas = res.content
      } finally {
        this.naturezaLoading = false
      }
    },

    setDateNowDate() {
      this.item.date = new Date()
    },
  },

  watch: {
    'item.unidades'(newVal) {
      if (this.item.departments?.length) {
        const valid = this.filteredDepartments.map((d) => d.id)
        this.item.departments = this.item.departments.filter((id) =>
          valid.includes(id)
        )
      }
      
      // Sempre recarregar centros de custo quando unidades mudarem
      this.getCostCenters()
    },

    'item.departments': {
      handler(newDepts) {
        if (!newDepts?.length) return
        if (!this.item.departmentCosts) this.item.departmentCosts = {}

        newDepts.forEach((id) => {
          if (this.item.departmentCosts[id] === undefined)
            this.$set(this.item.departmentCosts, id, 0)
        })

        Object.keys(this.item.departmentCosts).forEach((id) => {
          if (!newDepts.includes(parseInt(id)))
            this.$delete(this.item.departmentCosts, id)
        })
      },
      deep: true,
    },

    'item.costCenters': {
      handler(newCCs) {
        if (!newCCs?.length) return
        if (!this.item.costCenterCosts) this.item.costCenterCosts = {}

        newCCs.forEach((id) => {
          if (this.item.costCenterCosts[id] === undefined)
            this.$set(this.item.costCenterCosts, id, 0)
        })

        Object.keys(this.item.costCenterCosts).forEach((id) => {
          if (!newCCs.includes(parseInt(id)))
            this.$delete(this.item.costCenterCosts, id)
        })
      },
      deep: true,
    },

    'item.costCenterCosts': {
      handler(newCosts) {
        if (!newCosts || this.item.costCenters?.length <= 1) return

        Object.keys(newCosts).forEach((ccId) => {
          const currentValue = parseFloat(newCosts[ccId]) || 0
          const maxAvailable = this.getRemainingForCostCenter(parseInt(ccId))

          if (currentValue > maxAvailable) {
            this.$set(this.item.costCenterCosts, ccId, maxAvailable)
          }
        })
      },
      deep: true,
    },

    totalCostFromDepartments() {
      if (this.shouldDisableGeneralCost) {
        this.item.cost = this.totalCostFromDepartments
      }
    },
  },
}
</script>

<style></style>
